import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, filter, finalize, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { of, ReplaySubject } from 'rxjs';
import { LanguageService } from '../language.service';
import { FormsErrorHandlerService } from '../forms-error-handler.service';
import { CacheService } from '../cache/cache.service';
import { UserService } from './user.service';
import { ContentUpdaterService } from '../content-updater/content-updater.service';
import { ToastMessageService } from '../../shared/modules/toast-message/toast-message.service';
import { EnvironmentService } from '../environment.service';
import { Router } from '@angular/router';
import { BannerType } from '../../../../environments/environment';
import { BannersService } from '../banners.service';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { untilDestroyed } from 'ngx-unificator/rxjs';
import { PaymentMode, UserPaymentModeService } from './user-payment-mode.service';
import { PlatformGraphQlService } from '../graphql/platform-graphql.service';
import { ApolloError } from '@apollo/client/errors';
import { CookieService, LocalstorageService } from 'ngx-unificator/services';
import { PlatformService } from '../platform.service';
import { WindowService } from '../window.service';
import { StagDetectorService } from '../stag-detector.service';
import { ApolloClientErrorCodes } from '../graphql/errors';
import { GlobalFieldsConfig } from '../../helpers/global-field-config';
import { UserGroupsService } from './user-groups.service';
import { StaticContentService } from '../static-content.service';
import { resolveChain } from '../../helpers/resolve-chain';
import { LocalHistoryService } from '../local-history.service';
import { UserStorageRedirectService } from './user-storage-redirect.service';

export interface Banner {
  Title: string;
  LOCAL_STORAGE_STAG_URL_KEY;
  Description: string;
  Image: string;
  SubTitle?: string;
  Prize?: string;
}

/**
 * Countries to set EUR currency by default
 */
const eurCountries = [];

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class UserAuthService {
  public lang = inject(LanguageService);
  private _fb = inject(FormBuilder);
  private _formErrors = inject(FormsErrorHandlerService);
  private _cache = inject(CacheService);
  private _user = inject(UserService);
  private _contentUpdater = inject(ContentUpdaterService);
  private _toastMessage = inject(ToastMessageService);
  private _env = inject(EnvironmentService);
  private _router = inject(Router);
  private _banners = inject(BannersService);
  private _paymentMode = inject(UserPaymentModeService);
  private _platformApi = inject(PlatformGraphQlService);
  private _cookie = inject(CookieService);
  private _platform = inject(PlatformService);
  private _stag = inject(StagDetectorService);
  private _userGruop = inject(UserGroupsService);
  private _window = inject(WindowService);
  private _static = inject(StaticContentService);
  private _storage = inject(LocalstorageService);
  private _local = inject(LocalHistoryService);
  private _userRedirect = inject(UserStorageRedirectService);

  private _registerForm: FormGroup = this._fb.group({
    email: [null, [...GlobalFieldsConfig.email.validator]],
    password: [
      null,
      [...GlobalFieldsConfig.password.validator],
    ],
    username: [null, [Validators.required]],
    currency: [null],
    termsAndConditionsVersion: [true, [Validators.requiredTrue]],
    receivePromo: [true],
  });

  private _loginForm: FormGroup = this._fb.group({
    email: [null, [...GlobalFieldsConfig.email.validator]],
    password: [
      null,
      [...GlobalFieldsConfig.password.validator],
    ],
  });

  private _loading = false;

  private _bannerForm$: ReplaySubject<Banner> = this.getBanner$('register-form');
  private _bannerLeaveReg$: ReplaySubject<Banner> = this.getBanner$('register-leave');

  get loginForm(): FormGroup {
    return this._loginForm;
  }

  get registerForm(): FormGroup {
    return this._registerForm;
  }

  get loading(): boolean {
    return this._loading;
  }

  get bannerForm$(): ReplaySubject<Banner> {
    return this._bannerForm$;
  }

  get bannerLeaveReg$(): ReplaySubject<Banner> {
    return this._bannerLeaveReg$;
  }

  /**
   * Login form submit handler
   */
  onLoginSubmit() {
    if (this._loginForm.invalid) {
      this._formErrors.applyFormErrors(this._loginForm, null, true);
      return;
    }
    const data = { ...this._loginForm.value };
    data.email = data.email?.trim();
    this._loading = true;
    this._platformApi
      .usersSignIn(data)
      .pipe(
        switchMap(() => this._user.fetchAllUserData()),
        tap(res => {
          this._loginForm.reset();
          this._cache.clear();
          this._loading = false;
          this._contentUpdater.updateAll();
          if (this._platform.isBrowser) {
            this._cookie.set('registered', '1', 999, '/', window.location.hostname as any);
          }
          resolveChain(
            () => this._resolveCustomPagesRedirect(),
            () => this._regularLoginRedirect(),
          );
        }),
        first(),
        catchError((error: ApolloError) => {
          this._toastMessage.error('t.login-error');
          // if (error?.graphQLErrors[0]?.extensions?.classification === ApolloClientErrorCodes.NOT_FOUND) {
          //   this._toastMessage.error('t.invalid-email-password');
          //   this._loginForm.markAsTouched();
          // }
          this._loading = false;
          return of(error);
        }),
      )
      .subscribe();
  }

  onRegSubmit() {
    if (this._registerForm.invalid) {
      this._formErrors.applyFormErrors(this._registerForm, null, true);
      return;
    }
    this._loading = true;
    const data = { ...this._registerForm.value };
    data.email = data.email?.trim();
    data.termsAndConditionsVersion = 'TAC Version';
    data.privacyPolicyVersion = 'PP_Version';
    data.affiliateParams = this._stag.registerAffiliateParams;
    data.channel = this._stag.registerAffiliateParams?.length ? 'affiliate' : 'web';
    data.language = this.lang.current;

    if (Boolean(this._user.fastDepositSegment)) {
      this._fastDepositRegSubmit(data);
    } else {
      this._regSubmit(data);
    }
  }

  private _regSubmit(data: any) {
    this._platformApi
      .usersSignUp(data)
      .pipe(
        switchMap(() => this._user.fetchAllUserData()),
        tap(() => {
          this._cache.clear();
          this._contentUpdater.updateAll();
          if (this._platform.isBrowser) {
            this._cookie.set('registered', '1', 999, '/', window.location.hostname as any);
            this._user.checkUserAlreadyRegistered();
          }
          resolveChain(
            () => this._resolveCustomPagesRedirect(),
            () => this._resolveStagRedirect(),
            () => this._regularRedirect(),
          );
        }),
        catchError((error: ApolloError) => {
          switch (error?.graphQLErrors[0]?.extensions?.code || null) {
            case ApolloClientErrorCodes.PLAYER_ALREADY_EXISTS:
              this._toastMessage.error('t.register-error');
              break;
            default:
              this._toastMessage.error(error.message);
              break;
          }
          return of(error);
        }),
        finalize(() => (this._loading = false)),
      )
      .subscribe();
  }

  private _fastDepositRegSubmit(data: any) {
    this._platformApi
      .usersSignUp(data)
      .pipe(
        switchMap(() => this._userGruop.addToGroup(this._user.fastDepositSegment)),
        switchMap(() => this._user.fetchAllUserData()),
        tap(() => {
          this._cache.clear();
          this._contentUpdater.updateAll();
          if (this._platform.isBrowser) {
            this._cookie.set('registered', '1', 999, '/', window.location.hostname as any);
            this._user.checkUserAlreadyRegistered();
          }
          resolveChain(
            () => this._resolveCustomPagesRedirect(),
            () => this._resolveStagRedirect(),
            () => this._regularRedirect(),
          );
        }),
        catchError((error: ApolloError) => {
          switch (error?.graphQLErrors[0]?.extensions?.code || null) {
            case ApolloClientErrorCodes.PLAYER_ALREADY_EXISTS:
              this._toastMessage.error('t.register-error');
              break;
            default:
              this._toastMessage.error(error.message);
              break;
          }
          return of(error);
        }),
        finalize(() => (this._loading = false)),
      )
      .subscribe();
  }

  public getBanner$(alias: string) {
    return this._paymentMode.currentMode$.pipe(
      untilDestroyed(this),
      switchMap(mode =>
        this._banners.list({
          type: mode === PaymentMode.CRYPTO ? BannerType.REGISTER_BANNER : BannerType.REGISTER_BANNER_FIAT,
        }),
      ),
      filter(list => !!list),
      map((list: any[]) => list.find(e => e.alias === alias)),
      shareReplay(1),
    ) as ReplaySubject<any>;
  }

  /**
   * Set currency by default according payment mode
   * @private
   */
  private _setCurrencyByDefault() {
    if (eurCountries.includes(this._env.env.country.short)) {
      this._registerForm.get('currency').setValue('EUR');
    } else {
      this._registerForm.get('currency').setValue(this._env.env.currency.short || 'USD');
    }
  }

  public setDefaultCurrency() {
    this._setCurrencyByDefault();
  }

  public generateNick() {
    this._registerForm.get('username').setValue(this.generateNickname(10));
  }

  /**
   * Generate random username
   * @private
   */
  public generateNickname(length) {
    const randomChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
    }
    return result;
  }

  /**
   * The `logoutUser` function logs out the user, reloads the page in a browser environment, and calls
   * a callback function.
   * @param {CallableFunction} callback - The `callback` parameter is a function that will be called
   * after the user is successfully logged out.
   */
  public logoutUser(callback: CallableFunction) {
    this._platformApi.userLogout().subscribe(e => {
      if (e.data.result) {
        if (this._platform.isBrowser) {
          setTimeout(() => {
            this._window?.nativeWindow?.location.reload();
          }, 250);
        }
        return callback();
      }
    });
  }

  /**
   * Redirects the user to the '/deposit' route after a short delay, and displays success messages.
   * This is a private helper method used internally within the UserAuthService.
   */
  private _regularRedirect() {
    setTimeout(() => {
      this._router.navigate(['/deposit']).then(() => {
        this._toastMessage.success('t.register-success');
        this._toastMessage.success('t.confirm-your-email');
      });
    }, 100);
    return true;
  }

  private _regularLoginRedirect() {
    if (+this._user.currentWallet.amount < 1) {
      this._router.navigate(['/deposit']).then(() => {
        this._toastMessage.success('t.auth-success');
      });
    } else {
      this._router.navigate(['/']).then(() => {
        this._toastMessage.success('t.auth-success');
      });
    }
    return true;
  }

  private async _resolveCustomPagesRedirect() {
    if (this._userRedirect.getRedirectRoute()) {
      await this._router.navigate([this._userRedirect.getRedirectRoute()]);
      this._userRedirect.removeRedirectRoute();
      return true;
    }
    return false;
  }

  /**
   * Resolves the redirect for the "registration-redirect" slug by fetching the corresponding content and navigating to it.
   * If the content is found, it redirects the user to the specified URL and displays success messages.
   * Otherwise, it returns `false`.
   * @returns {Observable<boolean>} An observable that emits `true` if the redirect was successful, `false` otherwise.
   */
  private _resolveStagRedirect() {
    return this._static.item({ slug: 'registration-redirect' }).pipe(
      filter(data => !!data),
      first(),
      map(data => data[0]),
      map(data => {
        if (data?.Content) {
          setTimeout(() => {
            this._router.navigateByUrl(data?.Content).then(() => {
              this._toastMessage.success('t.register-success');
              this._toastMessage.success('t.confirm-your-email');
            });
          }, 100);
          return true;
        }
        return false;
      }),
    );
  }

}
